import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import '@/App.css';

// Landing Page Components
import Hero from './components/landing/Hero';
import Problem from './components/landing/Problem';
import ContentEngineFlow from './components/landing/ContentEngineFlow';
import ContentShowcase from './components/landing/ContentShowcase';
import UseCases from './components/landing/UseCases';
import CaseBlock from './components/landing/CaseBlock';
import HowItWorks from './components/landing/HowItWorks';
import FAQ from './components/landing/FAQ';
import FinalCTA from './components/landing/FinalCTA';
import Footer from './components/landing/Footer';

// Legal Pages
import { TermsPage, PrivacyPage, CookiesPage, NotFoundPage } from './components/landing/LegalPages';

// SEO Meta Component
const SEOHead = () => {
  React.useEffect(() => {
    document.title = 'BrandContentFlow — Turn One Post Into Content for Every Platform';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'BrandContentFlow helps you transform one piece of content into platform-ready posts for LinkedIn, Twitter, newsletters and more — automatically.';

    const ogTags = [
      { property: 'og:title', content: 'BrandContentFlow — Turn One Post Into Content for Every Platform' },
      { property: 'og:description', content: 'Transform one piece of content into platform-ready posts for every channel automatically.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://brandcontentflow.com' },
    ];

    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.content = tag.content;
    });

    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'BrandContentFlow — Turn One Post Into Content for Every Platform' },
      { name: 'twitter:description', content: 'Transform one piece of content into platform-ready posts for every channel automatically.' },
    ];

    twitterTags.forEach(tag => {
      let element = document.querySelector(`meta[name="${tag.name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.name = tag.name;
        document.head.appendChild(element);
      }
      element.content = tag.content;
    });

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'BrandContentFlow',
      applicationCategory: 'BusinessApplication',
      description: 'BrandContentFlow helps you transform one piece of content into platform-ready posts for LinkedIn, Twitter, newsletters and more — automatically.',
      url: 'https://brandcontentflow.com',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/PreOrder',
      },
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);

  }, []);

  return null;
};

// Landing Page - Sections in mandatory order
const LandingPage = () => {
  return (
    <main className="min-h-screen bg-white" data-testid="landing-page">
      <ContentEngineFlow />
      <Problem />
      <ContentShowcase />
      <UseCases />
      <CaseBlock />
      <HowItWorks />
      <FAQ />
      <FinalCTA />
      <Footer />
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <SEOHead />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
